import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type {
  EdgeMetadata,
  EdgeMetadataTable,
  FramingBoundaryWithMetadata,
  VertexMetadata,
  VertexMetadataTable,
} from "building-types/framing/framingMetaDataTypes";
import type { FramingPerimeterConvexSplitResult } from "building-types/framing/wallFramingConvexSplitTypes";
import type {
  Framing,
  GeneratedFramedWallAssembly,
  WallFramingInput,
  WallOpeningBoundary,
} from "building-types/framing/wallFramingTypes";
import type { Table } from "common/data/table";
import { distPointToSegment } from "geom/closestPoint";
import { Vec2PolyLine2ContainmentClass, classifyContainmentVec2PolyLine2 } from "geom/containment";
import { DIST_EPSILON, WindingDir } from "geom/core";
import { polygonIntersectSimple } from "geom/polygon";
import {
  polyLine2AABB2,
  polyLine2Close,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2IsClosed,
  polyLine2IsConvex,
  polyLine2MakeAxisAlignedRect,
  polyLine2SetCCW,
  polyLine2Vertex,
  polyLine2VertexCount,
  polyLine2WindingDir,
} from "geom/polyLine2";
import { closedPolyLine2New } from "geom/polyLine2BrandedTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import {
  collectBoundaryVertices,
  computeOpeningRanges,
  deriveOpeningLayoutBounds,
  framingDirectionVector,
  framingPerpendicularVector,
  layoutPointFromPerpParallel,
} from "../framingLayoutUtils";
import { WallFramingGenerator } from "./wallFraming";
import { computeWallFramingStartPt } from "./wallFramingUtils";

const DEFAULT_WALL_STUD_DIRECTION = Math.PI / 2;

export function isFramingPerimeterConvex(perimeter: FramingBoundaryWithMetadata): boolean {
  return polyLine2IsConvex(perimeter.polyline);
}

/**
 * Split a simple rectilinear CCW perimeter into axis-aligned convex polygons.
 * Recursively cuts at concave (reflex) vertices until every piece is convex.
 * Already-convex inputs are returned as a single area.
 */
export function splitFramingPerimeterIntoConvexAreas(
  perimeter: FramingBoundaryWithMetadata,
): FramingPerimeterConvexSplitResult {
  const polylines = splitPolyLine2IntoConvexParts(perimeter.polyline);
  const isConvex = polylines.length === 1 && isFramingPerimeterConvex(perimeter);
  return {
    isConvex,
    areas: polylines.map((polyline) => framingBoundaryFromPolyLine2(perimeter, polyline)),
  };
}

/**
 * Generate wall framing, splitting non-convex perimeters into convex areas first.
 * Shared split edges receive an end stud from each adjacent area (doubled studs).
 */
export function generateWallFramingWithConvexSplit(
  input: WallFramingInput,
  framingComponents: Table<FramingComponentEntity>,
): GeneratedFramedWallAssembly {
  const split = splitFramingPerimeterIntoConvexAreas(input.perimeter);
  if (split.areas.length <= 1) {
    return new WallFramingGenerator(input, framingComponents).generate();
  }

  const direction =
    typeof input.framingMetadata.direction === "number" ? input.framingMetadata.direction : DEFAULT_WALL_STUD_DIRECTION;

  const framing: Framing[] = [];
  const openingFramingErrors: string[] = [];
  let memberIdOffset = 0;

  const allOpenings = input.openings ?? [];
  const layoutFrame = resolveLayoutFrame(input);

  for (const area of split.areas) {
    const areaOpenings = filterOpeningsForConvexArea(allOpenings, area, layoutFrame);
    const areaInput: WallFramingInput = {
      ...input,
      perimeter: area,
      openings: areaOpenings,
      framingMetadata: {
        ...input.framingMetadata,
        framingStartPt: computeWallFramingStartPt(area.polyline, direction),
      },
    };
    const result = new WallFramingGenerator(areaInput, framingComponents, memberIdOffset).generate();
    memberIdOffset += result.framing.length;
    framing.push(...result.framing);
    if (result.openingFramingErrors) {
      openingFramingErrors.push(...result.openingFramingErrors);
    }
  }

  return {
    levelId: "debug-level",
    framing,
    openingFramingErrors: openingFramingErrors.length > 0 ? openingFramingErrors : undefined,
  };
}

function resolveLayoutFrame(input: WallFramingInput): {
  planeOrigin: Vec2;
  perpDirection: Vec2;
  framingDirection: Vec2;
} {
  const direction =
    typeof input.framingMetadata.direction === "number" ? input.framingMetadata.direction : DEFAULT_WALL_STUD_DIRECTION;
  return {
    planeOrigin: input.framingMetadata.framingStartPt,
    perpDirection: framingPerpendicularVector(direction),
    framingDirection: framingDirectionVector(direction),
  };
}

function filterOpeningsForConvexArea(
  openings: readonly WallOpeningBoundary[],
  area: FramingBoundaryWithMetadata,
  layoutFrame: {
    planeOrigin: Vec2;
    perpDirection: Vec2;
    framingDirection: Vec2;
  },
): WallOpeningBoundary[] {
  if (openings.length === 0) {
    return [];
  }

  const sortedVertices = collectBoundaryVertices(
    area,
    openings,
    layoutFrame.planeOrigin,
    layoutFrame.perpDirection,
    layoutFrame.framingDirection,
  );
  const { perpOpeningRanges, parallelOpeningRanges } = computeOpeningRanges(sortedVertices, openings.length);

  const filtered: WallOpeningBoundary[] = [];
  for (const perpRange of perpOpeningRanges) {
    const parallelRange = parallelOpeningRanges.find((r) => r.openingIndex === perpRange.openingIndex);
    if (!parallelRange) {
      continue;
    }
    const bounds = deriveOpeningLayoutBounds(perpRange.openingIndex, perpRange, parallelRange);
    const centerPerp = (bounds.startPerp + bounds.endPerp) / 2;
    const centerParallel = (bounds.bottomParallel + bounds.topParallel) / 2;
    const [cx, cy] = layoutPointFromPerpParallel(
      centerPerp,
      centerParallel,
      layoutFrame.planeOrigin,
      layoutFrame.perpDirection,
      layoutFrame.framingDirection,
    );
    const containment = classifyContainmentVec2PolyLine2(cx, cy, area.polyline);
    if (containment === Vec2PolyLine2ContainmentClass.INSIDE) {
      const opening = openings[perpRange.openingIndex];
      if (opening) {
        filtered.push(opening);
      }
    }
  }
  return filtered;
}

function splitPolyLine2IntoConvexParts(pl: PolyLine2): PolyLine2[] {
  if (polyLine2IsConvex(pl)) {
    return [ensureCCWClosed(pl)];
  }

  const concaveVertexIndex = findFirstConcaveVertexIndexCCW(pl);
  if (concaveVertexIndex === undefined) {
    return [ensureCCWClosed(pl)];
  }

  const splitParts = splitRectilinearAtConcaveVertex(pl, concaveVertexIndex);
  if (splitParts.length < 2) {
    return [ensureCCWClosed(pl)];
  }

  return splitParts.flatMap((part) => splitPolyLine2IntoConvexParts(part));
}

function findFirstConcaveVertexIndexCCW(pl: PolyLine2): number | undefined {
  const vertexCount = polyLine2VertexCount(pl) - 1;
  if (vertexCount < 3) {
    return undefined;
  }

  for (let i = 0; i < vertexCount; i++) {
    const prevIndex = (i - 1 + vertexCount) % vertexCount;
    const nextIndex = (i + 1) % vertexCount;
    const [px, py] = polyLine2Vertex(pl, prevIndex);
    const [cx, cy] = polyLine2Vertex(pl, i);
    const [nx, ny] = polyLine2Vertex(pl, nextIndex);
    const cross = (cx - px) * (ny - cy) - (cy - py) * (nx - cx);
    if (cross < -DIST_EPSILON) {
      return i;
    }
  }

  return undefined;
}

function splitRectilinearAtConcaveVertex(pl: PolyLine2, concaveVertexIndex: number): PolyLine2[] {
  const vertexCount = polyLine2VertexCount(pl) - 1;
  const prevEdgeIndex = (concaveVertexIndex - 1 + vertexCount) % vertexCount;
  const [vx, vy] = polyLine2Vertex(pl, concaveVertexIndex);
  const aabb = polyLine2AABB2(pl);

  // Rectilinear concave corners join one horizontal and one vertical edge — cut vertically through
  // the concave vertex (e.g. U-shapes → left leg, bottom bridge, right leg as separate bays).
  const incomingHorizontal = edgeIsHorizontal(pl, prevEdgeIndex);
  const outgoingHorizontal = edgeIsHorizontal(pl, concaveVertexIndex);
  const parts: PolyLine2[] = [];

  if (incomingHorizontal !== outgoingHorizontal) {
    if (vx > aabb.minX + DIST_EPSILON && vx < aabb.maxX - DIST_EPSILON) {
      const leftMask = polyLine2MakeAxisAlignedRect(aabb.minX, aabb.minY, vx, aabb.maxY);
      const rightMask = polyLine2MakeAxisAlignedRect(vx, aabb.minY, aabb.maxX, aabb.maxY);
      pushIntersectionParts(parts, pl, leftMask);
      pushIntersectionParts(parts, pl, rightMask);
    }
    return parts;
  }

  if (vy > aabb.minY + DIST_EPSILON && vy < aabb.maxY - DIST_EPSILON) {
    const bottomMask = polyLine2MakeAxisAlignedRect(aabb.minX, aabb.minY, aabb.maxX, vy);
    const topMask = polyLine2MakeAxisAlignedRect(aabb.minX, vy, aabb.maxX, aabb.maxY);
    pushIntersectionParts(parts, pl, bottomMask);
    pushIntersectionParts(parts, pl, topMask);
  }

  return parts;
}

function pushIntersectionParts(parts: PolyLine2[], source: PolyLine2, mask: PolyLine2): void {
  const loops = polygonIntersectSimple(source, mask);
  for (const loop of loops) {
    if (loop.length >= 6) {
      parts.push(ensureCCWClosed(loop));
    }
  }
}

function edgeIsHorizontal(pl: PolyLine2, edgeIndex: number): boolean {
  const edge = polyLine2Edge(pl, edgeIndex);
  return Math.abs(edge[3] - edge[1]) <= DIST_EPSILON;
}

function ensureCCWClosed(pl: PolyLine2): PolyLine2 {
  let copy = polyLine2IsClosed(pl) ? polyLine2Copy(pl) : polyLine2Close(polyLine2Copy(pl));
  if (polyLine2WindingDir(copy) !== WindingDir.CCW) {
    copy = polyLine2SetCCW(copy);
  }
  return copy;
}

function framingBoundaryFromPolyLine2(
  sourcePerimeter: FramingBoundaryWithMetadata,
  pl: PolyLine2,
): FramingBoundaryWithMetadata {
  const ccw = ensureCCWClosed(pl);
  return {
    polyline: closedPolyLine2New(polyLine2New([...ccw])),
    edgeMetadata: edgeMetadataForSplitPerimeter(sourcePerimeter, ccw),
    vertexMetadata: vertexMetadataForSplitPerimeter(sourcePerimeter, ccw),
  };
}

function edgeMetadataForSplitPerimeter(
  sourcePerimeter: FramingBoundaryWithMetadata,
  splitPolyline: PolyLine2,
): EdgeMetadataTable | undefined {
  if (!sourcePerimeter.edgeMetadata) {
    return undefined;
  }
  const edgeCount = polyLine2EdgeCount(splitPolyline);
  const mapped: Record<number, EdgeMetadata> = {};
  for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex++) {
    const sourceEdgeIndex = matchingSourceEdgeIndex(sourcePerimeter.polyline, splitPolyline, edgeIndex);
    if (sourceEdgeIndex === undefined) {
      continue;
    }
    const metadata = sourcePerimeter.edgeMetadata[sourceEdgeIndex];
    if (metadata) {
      mapped[edgeIndex] = JSON.parse(JSON.stringify(metadata)) as EdgeMetadata;
    }
  }
  return Object.keys(mapped).length > 0 ? mapped : undefined;
}

function vertexMetadataForSplitPerimeter(
  sourcePerimeter: FramingBoundaryWithMetadata,
  splitPolyline: PolyLine2,
): VertexMetadataTable | undefined {
  if (!sourcePerimeter.vertexMetadata) {
    return undefined;
  }
  const vertexCount = polyLine2VertexCount(splitPolyline) - 1;
  const mapped: Record<number, VertexMetadata> = {};
  for (let vertexIndex = 0; vertexIndex < vertexCount; vertexIndex++) {
    const [x, y] = polyLine2Vertex(splitPolyline, vertexIndex);
    const sourceVertexIndex = matchingSourceVertexIndex(sourcePerimeter.polyline, x, y);
    if (sourceVertexIndex === undefined) {
      continue;
    }
    const metadata = sourcePerimeter.vertexMetadata[sourceVertexIndex];
    if (metadata) {
      mapped[vertexIndex] = JSON.parse(JSON.stringify(metadata)) as VertexMetadata;
    }
  }
  return Object.keys(mapped).length > 0 ? (mapped as VertexMetadataTable) : undefined;
}

function matchingSourceEdgeIndex(
  sourcePolyline: PolyLine2,
  splitPolyline: PolyLine2,
  splitEdgeIndex: number,
): number | undefined {
  const splitEdge = polyLine2Edge(splitPolyline, splitEdgeIndex);
  const midX = (splitEdge[0] + splitEdge[2]) / 2;
  const midY = (splitEdge[1] + splitEdge[3]) / 2;
  const sourceEdgeCount = polyLine2EdgeCount(sourcePolyline);
  let bestIndex: number | undefined;
  let bestDistance = Infinity;
  for (let sourceEdgeIndex = 0; sourceEdgeIndex < sourceEdgeCount; sourceEdgeIndex++) {
    const sourceEdge = polyLine2Edge(sourcePolyline, sourceEdgeIndex);
    const distance = distPointToSegment(midX, midY, sourceEdge[0], sourceEdge[1], sourceEdge[2], sourceEdge[3]);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = sourceEdgeIndex;
    }
  }
  if (bestIndex === undefined || bestDistance > DIST_EPSILON) {
    return undefined;
  }
  return bestIndex;
}

function matchingSourceVertexIndex(sourcePolyline: PolyLine2, x: number, y: number): number | undefined {
  const vertexCount = polyLine2VertexCount(sourcePolyline) - 1;
  for (let vertexIndex = 0; vertexIndex < vertexCount; vertexIndex++) {
    const [sx, sy] = polyLine2Vertex(sourcePolyline, vertexIndex);
    if (Math.abs(sx - x) <= DIST_EPSILON && Math.abs(sy - y) <= DIST_EPSILON) {
      return vertexIndex;
    }
  }
  return undefined;
}
